/** Which saved broker view is active, remembered across a page refresh.
 *  Kept out of SavedFilterSets.tsx so it can be unit-tested and shared without
 *  tripping react-refresh's component-only export rule. */

export type SavedViewScope = 'personal' | 'global';

export interface ActiveSavedViewRef {
  name: string;
  scope: SavedViewScope;
}

export const ACTIVE_VIEW_STORAGE_KEY = 'sift.brokerActiveView';

export function readActiveViewRef(): ActiveSavedViewRef | null {
  try {
    const raw = localStorage.getItem(ACTIVE_VIEW_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.name !== 'string' || !v.name.trim()) return null;
    return { name: v.name, scope: v.scope === 'global' ? 'global' : 'personal' };
  } catch {
    return null;
  }
}

export function writeActiveViewRef(ref: ActiveSavedViewRef | null): void {
  try {
    if (ref) localStorage.setItem(ACTIVE_VIEW_STORAGE_KEY, JSON.stringify({ name: ref.name, scope: ref.scope }));
    else localStorage.removeItem(ACTIVE_VIEW_STORAGE_KEY);
  } catch {
    /* storage blocked — the view just won't survive a refresh */
  }
}

/**
 * Order-insensitive fingerprint of a filter selection, so "is the board still
 * exactly this saved view?" ignores the order chips were clicked in and the
 * defaults legacy sets omit.
 */
export function selectionFingerprint(sel: object): string {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(sel as Record<string, unknown>)) {
    if (k === 'name') continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.map(String).sort();
    } else if (typeof v === 'string') {
      const t = v.trim();
      if (!t || (k === 'conditionMode' && t === 'exclude')) continue;
      out[k] = t;
    } else if (v != null) {
      out[k] = v;
    }
  }
  // Include/exclude means nothing without a condition picked.
  if (!out.conditions) delete out.conditionMode;
  return JSON.stringify(Object.keys(out).sort().map((k) => [k, out[k]]));
}
